/**
 * Loads the signed-in user's own AI spend for the Usage page.
 */
import { useCallback, useEffect, useState } from 'react';

import { logger } from '@/utils/logger';

import { accountUsageApi, type AccountUsage } from '../api/accountUsageApi';

interface UseAccountUsageReturn {
  usage: AccountUsage | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Fetch the current user's spend on mount, with a manual refresh.
 *
 * @returns The usage, loading and error state, and a refresh action.
 */
export function useAccountUsage(): UseAccountUsageReturn {
  const [usage, setUsage] = useState<AccountUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sets state only once the request settles, so the mount effect never sets
  // state synchronously.
  const load = useCallback(
    () =>
      accountUsageApi
        .getUsage()
        .then(setUsage)
        .catch((err: unknown) => {
          logger.error('Failed to load account usage:', err);
          setError('Could not load your usage.');
        })
        .then(() => setIsLoading(false)),
    []
  );

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    return load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  return { usage, isLoading, error, refresh };
}
